import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { PROJECT_MEMBERS_CONFIG, PROJECT_MANAGERS_CONFIG, PROJECT_OWNERS_CONFIG } from '@rappider/shared/configs';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { ProjectInterface } from '@rappider/api-sdk';
import { ProjectRoleEvents } from './models/project-role-events.enum';
import { ASSIGN_MANAGER_CONFIG, UNASSIGN_MANAGER_CONFIG } from './models/project-members';
import { ColorService, NotificationService } from '@rappider/services';
import { Person, PersonWithRelations } from '@rappider/rappider-sdk';
import { Authority, defaultToolbarTitleHeadingSize, PATH_DEFINITIONS } from '@rappider/shared/definitions';
import { HeadingComponentConfig } from '@rappider/rappider-components/utils';
import { AssignManager, UnassignManager } from '../../states/project-member-state/project-member.actions';
import { selectCurrentUserProjectRole } from '../../states/selectors/user-role.selector';
import { SelectTabService } from '../../services/select-tab.service';

@Component({
  selector: 'rappider-project-members',
  templateUrl: './project-members.component.html',
  styleUrls: ['./project-members.component.scss']
})
export class ProjectMembersComponent implements OnInit, OnDestroy {

  isLoading = false;
  activeProject: ProjectInterface;
  members = [];
  owners = [];
  managers = [];
  mainTitle: HeadingComponentConfig;
  title = [];
  PROJECT_MEMBERS_CONFIG = PROJECT_MEMBERS_CONFIG;
  PROJECT_MANAGERS_CONFIG = PROJECT_MANAGERS_CONFIG;
  PROJECT_OWNERS_CONFIG = PROJECT_OWNERS_CONFIG;
  ASSIGN_MANAGER_CONFIG = ASSIGN_MANAGER_CONFIG;
  UNASSIGN_MANAGER_CONFIG = UNASSIGN_MANAGER_CONFIG;
  subscriptions: Subscription[] = [];
  displayToolbar = false;
  displayToolbarBackButton = false;

  projectMembers: PersonWithRelations[];
  activeUserRole: any;
  activeTab: string = 'Members';
  selectedIndex: number = 2;

  constructor(
    private store: Store<any>,
    private notificationService: NotificationService,
    private colorService: ColorService,
    private cdr: ChangeDetectorRef,
    private selectTabService: SelectTabService
  ) { }

  ngOnInit(): void {
    this.subscriptions = [
      this.subscribeToActiveProject(),
      this.subscribeToProjectMembers(),
      this.subscribeToUserRole(),
    ];
    this.subscribeToActiveMemberTab();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  subscribeToActiveMemberTab() {
    this.subscriptions.push(
      this.selectTabService.activeTab$.subscribe(tabKey => {
        if (tabKey && ['members', 'owners', 'managers'].includes(tabKey)) {
          this.activeTab = tabKey;
          this.selectedIndex = tabKey === 'members' ? 2 : tabKey === 'owners' ? 0 : 1;
        }
      })
    );
  }

  subscribeToActiveProject() {
    return this.store.select(state => state.activeProject.data).subscribe((activeProject: ProjectInterface) => {
      /* get active project */
      this.activeProject = activeProject;
      if (activeProject) {
        /* set component title */
        this.mainTitle = {
          content: 'SHARED.MEMBERS',
          type: defaultToolbarTitleHeadingSize
        };
        this.title = [
          {
            label: activeProject.name,
            redirectUrl: `${PATH_DEFINITIONS.PROJECTS.PROJECT_DETAIL_PATH}/${activeProject?.id}`
          },
          {
            label: 'SHARED.MEMBERS'
          }
        ];
      }
    });
  }

  subscribeToProjectMembers() {
    return this.store.select(state => state.projectMember?.data).subscribe((projectMembers: any[]) => {
      if (projectMembers) {
        this.projectMembers = projectMembers;
        this.getMembers();
      }
    });
  }

  subscribeToUserRole() {
    return this.store.select(selectCurrentUserProjectRole).subscribe(userRole => {
      this.activeUserRole = userRole;
      if (this.owners.length > 0 || this.managers.length > 0 || this.members.length > 0) {
        this.checkPersonAuth();
      }
    });
  }

  getMembers() {
    if (!this.projectMembers) {
      this.notificationService.createNotification(
        'error',
        'SHARED.ERROR',
        'PROJECT_MODULE.PROJECT_MEMBERS_COMPONENT.COULD_NOT_GET_MEMBERS'
      );
      return;
    }

    this.owners = [];
    this.managers = [];
    this.members = [];

    const getHighestAuthority = (authorities: any[] = []) => {
      const authorityOrder = [
        { name: Authority.ProjectOwner, label: 'Owner' },
        { name: Authority.ProjectManager, label: 'Manager' },
        { name: Authority.ProjectMember, label: 'Member' }
      ];
      for (const auth of authorityOrder) {
        if (authorities.some(a => a.name === auth.name)) {
          return auth;
        }
      }
      return { name: Authority.ProjectMember, label: 'Member' };
    };

    this.projectMembers.forEach(member => {
      const authorities = member.authorities || [];
      const highestAuth = getHighestAuthority(authorities);

      const roleColor = this.colorService.getColorForRole(highestAuth.name);
      const memberWithRole = {
        ...member,
        roleNames: [highestAuth.label],
        metadata: {
          color: roleColor
        }
      };

      switch (highestAuth.name) {
        case Authority.ProjectOwner:
          this.owners.push(memberWithRole);
          break;
        case Authority.ProjectManager:
          this.managers.push(memberWithRole);
          break;
        case Authority.ProjectMember:
          this.members.push(memberWithRole);
          break;
        default:
          this.members.push(memberWithRole);
          break;
      }
    });

    this.owners = [...this.owners];
    this.managers = [...this.managers];
    this.members = [...this.members];

    this.checkPersonAuth();
  }

  checkPersonAuth() {
    this.PROJECT_MANAGERS_CONFIG.itemActions = [];
    this.PROJECT_MEMBERS_CONFIG.itemActions = [];

    if (!this.activeUserRole) {
      return;
    }

    const isProjectOwner = this.activeUserRole.isProjectOwner;

    if (isProjectOwner) {
      this.PROJECT_MEMBERS_CONFIG.itemActions = [
        ...this.PROJECT_MEMBERS_CONFIG.itemActions,
        this.ASSIGN_MANAGER_CONFIG
      ];

      this.PROJECT_MANAGERS_CONFIG.itemActions = [
        ...this.PROJECT_MANAGERS_CONFIG.itemActions,
        this.UNASSIGN_MANAGER_CONFIG,
      ];
    }

    this.PROJECT_MEMBERS_CONFIG = { ...this.PROJECT_MEMBERS_CONFIG };
    this.PROJECT_MANAGERS_CONFIG = { ...this.PROJECT_MANAGERS_CONFIG };

    this.cdr.detectChanges();
  }

  onItemClick(event) {
    switch (event.action.name) {
      case ProjectRoleEvents.AssignManager:
        this.assignManager(event.data);
        break;
      case ProjectRoleEvents.UnassignManager:
        this.unassignManager(event.data);
        break;
      default:
        break;
    }
  }

  // #region ASSIGN AND UNASSIGN AS MANAGER

  assignManager(person: Person) {
    this.store.dispatch(new AssignManager({ personId: person.id }));
  }

  unassignManager(person: Person) {
    this.store.dispatch(new UnassignManager({ personId: person.id }));
  }

  // #endregion ASSIGN AND UNASSIGN AS MANAGER

}
